import { useApolloClient } from "@apollo/client";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { includes } from "ramda";
import { isNotNil } from "ramda-adjunct";
import { Recipient } from "./queries.graphql";
import { RecipientFragment } from "./queries.types";

export const useRecipientsChannelsAvailability = () => {
  const client = useApolloClient();

  /**
   * Reads the cached `Recipient` fragment for each id. Ids whose user has not
   * been loaded into the Apollo cache yet are skipped, so the result only
   * contains recipients whose channel availability is actually known.
   */
  const getKnownRecipients = (recipients: string[]) =>
    recipients
      .map((id) =>
        client.readFragment<RecipientFragment>({
          id: `User:${id}`,
          fragment: Recipient,
        })
      )
      .filter(isNotNil);

  const supportsChannel = (
    { availableNotificationChannels }: RecipientFragment,
    channels: NonNullable<NotificationChannel>[]
  ) => channels.some((sendingChannel) => includes(sendingChannel, availableNotificationChannels ?? []));

  /**
   * Returns the ids of the given recipients that can receive on at least one of
   * the selected channels. Recipients not yet in the cache are excluded, so use
   * this only for building the actual send payload.
   */
  const getAvailableRecipients = (recipients: string[], channels: NonNullable<NotificationChannel>[]) =>
    getKnownRecipients(recipients)
      .filter((recipient) => supportsChannel(recipient, channels))
      .map(({ id }) => id);

  /**
   * Validation-friendly check: whether at least one selected recipient can
   * receive on the chosen channels. Returns `true` while availability is still
   * unknown (no recipient loaded into the cache yet) so validation does not fail
   * during the initial async load - `.min(1)` still guarantees a recipient is
   * selected, and the send payload is filtered by `getAvailableRecipients`.
   */
  const hasSendableRecipient = (recipients: string[], channels: NonNullable<NotificationChannel>[]) => {
    const knownRecipients = getKnownRecipients(recipients);
    return knownRecipients.length === 0 || knownRecipients.some((recipient) => supportsChannel(recipient, channels));
  };

  return { getAvailableRecipients, hasSendableRecipient };
};
